import { defineStore } from "pinia";
import { recommendationCacheKey } from "@/libs/ai/recommendationCacheKey";
import { readSizingJobs, writeSizingJobs } from "@/libs/ai/activeSizingJobs";
import { fetchAINarrativeStatus } from "../libs/api/aiEquipmentNarrative";
import type {
  AINarrativeResponse,
  AINarrativeContent,
} from "../libs/api/aiEquipmentNarrative";
import type {
  AIEquipmentIssue,
  AIEquipmentSuggestionOption,
  AIEquipmentSuggestionsContext,
  AIEquipmentSuggestionsResponse,
} from "../libs/api/aiEquipmentSuggestions";

type AIPriority = "performance" | "runlife" | "cost";

const PRIORITIES: AIPriority[] = ["performance", "runlife", "cost"];

function getEquipmentId(
  equipment: Record<string, unknown> | undefined,
): string | undefined {
  const id =
    equipment?.id ??
    equipment?.material_id ??
    equipment?.erpMaterialNumber ??
    equipment?.seriesModel ??
    equipment?.model;
  return typeof id === "string" && id.length > 0 ? id : undefined;
}

function normalizeEnhancementIds(resp: {
  enhancementIds?: Record<string, string> | null;
  enhancementId?: string | null;
  selectedPriority?: string;
}): Partial<Record<AIPriority, string>> | null {
  if (resp.enhancementIds) return resp.enhancementIds;
  if (!resp.enhancementId) return null;
  const priority = PRIORITIES.includes(resp.selectedPriority as AIPriority)
    ? (resp.selectedPriority as AIPriority)
    : "performance";
  return { [priority]: resp.enhancementId };
}

export interface AISuggestion {
  id: string;
  priority: string;
  score: number;
  selected_pump_id?: string;
  selected_intake_id?: string;
  selected_seal_id?: string;
  selected_cable_id?: string;
  // Reserved for future expansion
  selected_motor_id?: string;
  selected_sensor_id?: string;
  selected_mle_id?: string;
  selected_motor_load_ratio?: number; // derived from equipment suggestion motor estimatedLoadHP / ratedHP
  selected_thermal_margin_f?: number; // placeholder; backend does not yet provide thermal margin
}

interface AISuggestionState {
  durableJobs: Record<string, string>;
  completedJobScenarios: Record<string, boolean>;
  recommendationCache: Record<string, {
    response: AIEquipmentSuggestionsResponse;
    savedAt: string;
    snapshot?: Partial<AISuggestionState>;
  }>;
  activeCacheKey: string | null;
  usingCachedResults: boolean;
  // Equipment suggestions (new endpoint) integration
  equipmentOptions?: {
    performance?: AIEquipmentSuggestionOption;
    runlife?: AIEquipmentSuggestionOption;
    cost?: AIEquipmentSuggestionOption;
  } | null;
  equipmentContext?: AIEquipmentSuggestionsContext | null;
  equipmentIssues: AIEquipmentIssue[];
  equipmentRequestId: number;
  loading: boolean;
  activeJobId: string | null;
  progress: { stage?: string; candidate?: number; verified?: number; elapsedSeconds?: number } | null;
  error: string | null;
  priority: string;
  warnings: string[];
  lastUpdated: string | null;
  appliedPriority?: string | null;
  appliedSuggestion?: AISuggestion | null; // minimal applied suggestion reference
  // Enhancement (narrative) polling fields
  enhancementId?: string | null;
  enhancementIds?: {
    performance?: string;
    runlife?: string;
    cost?: string;
  } | null;
  narrative?: AINarrativeContent | string | null;
  narrativePending?: boolean;
  enhancementStatus?: Record<string, AINarrativeResponse> | null;
  enhancementLoading?: Record<string, boolean> | null;
  enhancementError?: Record<string, string | null> | null;
  enhancementAttempts?: Record<string, number> | null;
  enhancementDone?: Record<string, boolean> | null;
  narratives?: Record<string, AINarrativeContent | string | null> | null;
  candidateNotes?: Record<string, Record<string, any> | null> | null;
  missingData?: Record<string, Array<Record<string, any>> | null> | null;
  tokenUsagePrompt?: Record<string, number | null> | null;
  tokenUsageCompletion?: Record<string, number | null> | null;
  latencyMs?: Record<string, number | null> | null;
  modelId?: Record<string, string | null> | null;
}

export const useAISuggestionsStore = defineStore("aiSuggestions", {
  state: (): AISuggestionState => ({
    durableJobs: readSizingJobs(),
    completedJobScenarios: {},
    recommendationCache: {},
    activeCacheKey: null,
    usingCachedResults: false,
    equipmentOptions: null,
    equipmentContext: null,
    equipmentIssues: [],
    equipmentRequestId: 0,
    loading: false,
    activeJobId: null,
    progress: null,
    error: null,
    priority: "performance",
    warnings: [],
    lastUpdated: null,
    enhancementId: null,
    enhancementIds: null,
    narrative: null,
    narrativePending: false,
    enhancementStatus: {},
    enhancementLoading: {},
    enhancementError: {},
    enhancementAttempts: {},
    enhancementDone: {},
    narratives: {},
    candidateNotes: {},
    missingData: {},
    tokenUsagePrompt: {},
    tokenUsageCompletion: {},
    latencyMs: {},
    modelId: {},
  }),
  actions: {
    async cancelEquipmentJob() {
      if (!this.activeJobId || !this.activeCacheKey) return;
      const requestId = this.equipmentRequestId;
      const scenarioId = this.equipmentContext?.scenarioId;
      if (!scenarioId) return;
      const { cancelAIEquipmentJob } = await import("../libs/api/aiEquipmentSuggestions");
      await cancelAIEquipmentJob(scenarioId, this.activeJobId);
      if (requestId !== this.equipmentRequestId) return;
      this.equipmentRequestId += 1;
      this.loading = false;
      this.activeJobId = null;
      delete this.durableJobs[scenarioId];
      writeSizingJobs(this.durableJobs);
      this.error = "Calculation cancelled. Generate again when ready.";
    },
    applySuggestion(priority: string) {
      const equipOpt = this.equipmentOptions
        ? (this.equipmentOptions as any)[priority]
        : null;
      let suggestion = null;
      if (equipOpt) {
        const motor = equipOpt.equipment.motor;
        let motorLoadRatio =
          motor && motor.ratedHP
            ? motor.estimatedLoadHP / motor.ratedHP
            : undefined;
        if (motorLoadRatio != null) {
          // Defensive clamp to [0, 2] (allow some overload visibility, will warn if >1)
          if (motorLoadRatio < 0) motorLoadRatio = 0;
          if (motorLoadRatio > 2) motorLoadRatio = 2;
        }
        suggestion = {
          id: `equip-${priority}`,
          priority,
          score:
            (equipOpt.metrics.score ?? equipOpt.metrics.efficiency ?? 0) / 100,
          selected_motor_load_ratio: motorLoadRatio,
          selected_thermal_margin_f: undefined,
          selected_pump_id: getEquipmentId(equipOpt.equipment.pumps?.[0]),
          selected_intake_id: getEquipmentId(equipOpt.equipment.intake),
          selected_seal_id: getEquipmentId(equipOpt.equipment.seal),
          selected_cable_id: getEquipmentId(equipOpt.equipment.cable),
          selected_motor_id: getEquipmentId(equipOpt.equipment.motor),
          selected_sensor_id: getEquipmentId(equipOpt.equipment.sensor),
          selected_mle_id: getEquipmentId(equipOpt.equipment.mle),
          selected_surface_cable_id: getEquipmentId(
            equipOpt.equipment.surfaceCable,
          ),
          selected_vsd_id: getEquipmentId(equipOpt.equipment.vsd),
          selected_switchboard_id: getEquipmentId(
            equipOpt.equipment.switchboard,
          ),
          selected_step_up_transformer_id: getEquipmentId(
            equipOpt.equipment.stepUpTransformer,
          ),
          selected_step_down_transformer_id: getEquipmentId(
            equipOpt.equipment.stepDownTransformer,
          ),
        } as any;
      }
      this.appliedPriority = suggestion ? priority : null;
      this.appliedSuggestion = suggestion;
      return suggestion;
    },
    async loadEquipmentSuggestions(
      payload: import("../libs/api/aiEquipmentSuggestions").AIEquipmentSuggestionsRequest,
      options: { force?: boolean; inputIdentity?: unknown } = {},
    ) {
      const key = recommendationCacheKey(payload, options.inputIdentity);
      // Reopening during a calculation attaches to the existing store state.
      if (this.loading && this.activeCacheKey === key) return null;
      // Preserve narrative/polling results as well as the recommendation itself.
      if (this.activeCacheKey && this.recommendationCache[this.activeCacheKey]) {
        const { recommendationCache, activeCacheKey, usingCachedResults, durableJobs, completedJobScenarios,
          equipmentRequestId, loading, error, ...snapshot } = this.$state;
        this.recommendationCache[this.activeCacheKey].snapshot =
          JSON.parse(JSON.stringify(snapshot));
      }
      const cached = this.recommendationCache[key];
      if (!options.force && cached) {
        if (this.activeCacheKey !== key) {
          this.equipmentRequestId += 1;
          this.cancelEnhancementPolling();
          this.$patch(state => Object.assign(state,
            JSON.parse(JSON.stringify(cached.snapshot || {}))
          ));
        }
        this.activeCacheKey = key;
        this.loading = false;
        this.error = null;
        this.usingCachedResults = true;
        if (this.narrativePending) this.startEnhancementPolling();
        return cached.response;
      }
      // Explicit reruns discard the previous entry, including on failure.
      delete this.recommendationCache[key];
      this.activeCacheKey = key;
      this.usingCachedResults = false;
      const requestId = ++this.equipmentRequestId;
      const requestedScenarioId = payload.scenarioId;
      if (
        this.equipmentContext?.scenarioId &&
        this.equipmentContext.scenarioId !== requestedScenarioId
      ) {
        this.cancelEnhancementPolling();
        this.equipmentOptions = null;
        this.equipmentContext = null;
        this.equipmentIssues = [];
        this.appliedPriority = null;
        this.appliedSuggestion = null;
        this.enhancementId = null;
        this.enhancementIds = null;
        this.narrative = null;
        this.narratives = {};
      }
      this.loading = true;
      this.activeJobId = null;
      this.progress = { stage: "Submitting equipment calculation", elapsedSeconds: 0 };
      this.error = null;
      this.cancelEnhancementPolling();
      this.enhancementId = null;
      this.enhancementIds = null;
      this.narrative = null;
      this.narrativePending = false;
      this.enhancementStatus = {};
      this.enhancementLoading = {};
      this.enhancementError = {};
      this.enhancementAttempts = {};
      this.enhancementDone = {};
      this.narratives = {};
      this.candidateNotes = {};
      this.missingData = {};
      this.tokenUsagePrompt = {};
      this.tokenUsageCompletion = {};
      this.latencyMs = {};
      this.modelId = {};
      this.equipmentOptions = null;
      this.equipmentContext = null;
      this.equipmentIssues = [];
      this.warnings = [];
      try {
        const {
          fetchAIEquipmentSuggestions,
          pickOptionByPriority,
          aggregateWarnings,
        } = await import("../libs/api/aiEquipmentSuggestions");

        // Inject enhanceNarrative flag if enabled in env
        const enableNarrative =
          import.meta.env.VITE_ENABLE_LLM_NARRATIVE !== "false";
        if (options.force && requestedScenarioId && this.completedJobScenarios[requestedScenarioId]) {
          delete this.durableJobs[requestedScenarioId];
          delete this.completedJobScenarios[requestedScenarioId];
          writeSizingJobs(this.durableJobs);
        }
        const existingJob = requestedScenarioId ? this.durableJobs[requestedScenarioId] : undefined;
        // Reconnect even on Retry while a job handle exists; never create a
        // duplicate because monitoring failed or the modal was closed.
        const req = { ...payload, enhanceNarrative: enableNarrative, ...(existingJob ? { jobId: existingJob } : {}) };

        const resp = await fetchAIEquipmentSuggestions(req, progress => {
          if (requestId === this.equipmentRequestId) {
            const { partialOptions, context, jobId, ...status } = progress;
            this.progress = status;
            if (jobId) {
              this.activeJobId = jobId;
              if (requestedScenarioId) {
                this.durableJobs[requestedScenarioId] = jobId;
                writeSizingJobs(this.durableJobs);
              }
            }
            if (!this.equipmentContext) this.equipmentContext = { scenarioId: requestedScenarioId };
            if (partialOptions) this.equipmentOptions = { ...this.equipmentOptions, ...partialOptions };
            if (context) this.equipmentContext = context;
          }
        }, () => requestId !== this.equipmentRequestId);
        if (requestId !== this.equipmentRequestId) return null;
        if (requestedScenarioId) {
          this.completedJobScenarios[requestedScenarioId] = true;
        }
        this.equipmentOptions = resp.options;
        this.equipmentContext = resp.context;
        this.equipmentIssues = resp.equipmentIssues || [];
        // Capture enhancement meta
        this.enhancementIds = normalizeEnhancementIds(resp) as any;
        this.enhancementId =
          this.enhancementIds?.[resp.selectedPriority as AIPriority] ||
          this.enhancementIds?.performance ||
          null;
        this.narrativePending = !!resp.narrativePending;
        const chosen = pickOptionByPriority(resp, resp.selectedPriority);
        this.warnings = aggregateWarnings(resp, chosen);
        this.lastUpdated = new Date().toISOString();
        if (Object.values(resp.options || {}).some(Boolean)) {
          this.recommendationCache[key] = { response: resp, savedAt: this.lastUpdated };
          const entries = Object.keys(this.recommendationCache);
          while (entries.length > 5) delete this.recommendationCache[entries.shift()!];
        }
        // Auto-start enhancement polling if pending
        if (this.enhancementIds && this.narrativePending) {
          this.startEnhancementPolling();
        }
        return resp;
      } catch (e: any) {
        if (requestId === this.equipmentRequestId) {
          if (requestedScenarioId && [401, 403, 404, 422].includes(e.status)) {
            delete this.durableJobs[requestedScenarioId];
            writeSizingJobs(this.durableJobs);
          }
          this.error = e.message || String(e);
        }
        return null;
      } finally {
        if (requestId === this.equipmentRequestId) {
          this.loading = false;
          this.activeJobId = null;
        }
      }
    },
    async startEnhancementPolling(
      opts: {
        intervalMs?: number;
        maxIntervalMs?: number;
        timeoutMs?: number;
        jitterRatio?: number;
      } = {},
    ) {
      if (!this.enhancementIds) return;
      const equipmentRequestId = this.equipmentRequestId;

      const self: any = this as any;
      const { usePoller } = await import("@/composables/usePoller");

      // Initialize tracking maps if needed
      if (!this.enhancementLoading) this.enhancementLoading = {};
      if (!this.enhancementError) this.enhancementError = {};
      if (!this.enhancementStatus) this.enhancementStatus = {};
      if (!this.enhancementAttempts) this.enhancementAttempts = {};
      if (!this.enhancementDone) this.enhancementDone = {};
      if (!this.narratives) this.narratives = {};
      if (!this.candidateNotes) this.candidateNotes = {};
      if (!this.missingData) this.missingData = {};
      if (!this.tokenUsagePrompt) this.tokenUsagePrompt = {};
      if (!this.tokenUsageCompletion) this.tokenUsageCompletion = {};
      if (!this.latencyMs) this.latencyMs = {};
      if (!this.modelId) this.modelId = {};

      // Start polling for each priority
      const priorities = PRIORITIES;
      for (const priority of priorities) {
        const enhancementId = this.enhancementIds[priority];
        if (!enhancementId) continue;

        // Skip if already running
        if (self[`_enhancementPoller_${priority}`]?.isRunning?.()) continue;

        this.enhancementLoading![priority] = true;
        this.enhancementError![priority] = null;

        const intervalMs = opts.intervalMs;
        const maxIntervalMs = opts.maxIntervalMs;
        const timeoutMs = opts.timeoutMs;
        const jitterRatio = opts.jitterRatio;

        self[`_enhancementPoller_${priority}`] = usePoller<AINarrativeResponse>(
          {
            task: async () => {
              return fetchAINarrativeStatus(enhancementId);
            },
            isDone: (r) => r.status === "completed",
            isError: (r) => r.status === "failed",
            intervalMs: intervalMs ?? 1000,
            maxIntervalMs: maxIntervalMs ?? 5000,
            timeoutMs: timeoutMs ?? 60000,
            jitterRatio: jitterRatio ?? 0.1,
            onUpdate: (r, attempt) => {
              if (equipmentRequestId !== this.equipmentRequestId) return;
              this.enhancementStatus![priority] = r;
              this.enhancementAttempts![priority] = attempt;
            },
            onSuccess: (r, attempt) => {
              if (equipmentRequestId !== this.equipmentRequestId) return;
              this.enhancementStatus![priority] = r;
              this.enhancementAttempts![priority] = attempt;
              this.enhancementDone![priority] = true;
              this.enhancementLoading![priority] = false;
              this.narratives![priority] = r.narrative || null;
              if (priority === (this.appliedPriority || "performance")) {
                this.narrative = r.narrative || null;
              }
              this.candidateNotes![priority] = r.candidateNotes || null;
              this.missingData![priority] = r.missingData || null;
              this.tokenUsagePrompt![priority] =
                r.tokenUsage?.prompt ?? (r as any).tokenUsagePrompt ?? null;
              this.tokenUsageCompletion![priority] =
                r.tokenUsage?.completion ??
                (r as any).tokenUsageCompletion ??
                null;
              this.latencyMs![priority] = r.latencyMs || null;
              this.modelId![priority] = r.modelId || null;

              // Check if all are done
              const allDone = priorities.every(
                (p) => !this.enhancementIds![p] || this.enhancementDone![p],
              );
              if (allDone) {
                this.narrativePending = false;
              }

              if (
                Array.isArray((r as any).warnings) &&
                (r as any).warnings.length
              ) {
                const merged = new Set<string>(this.warnings);
                (r as any).warnings.forEach((w: string) => merged.add(w));
                this.warnings = Array.from(merged.values()).sort();
              }
            },
            onError: (errLike, attempt) => {
              if (equipmentRequestId !== this.equipmentRequestId) return;
              const r =
                errLike && typeof errLike === "object" && "status" in errLike
                  ? (errLike as any)
                  : null;
              if (r && r.status === "failed") {
                this.enhancementStatus![priority] = r;
                this.enhancementError![priority] =
                  r.errorMessage || "Enhancement failed";
                this.narratives![priority] = null;
                if (priority === (this.appliedPriority || "performance")) {
                  this.narrative = null;
                }
              } else {
                this.enhancementError![priority] =
                  errLike instanceof Error ? errLike.message : String(errLike);
              }
              this.enhancementAttempts![priority] = attempt;
              this.enhancementLoading![priority] = false;
              this.enhancementDone![priority] = true;

              // Check if all are done
              const allDone = priorities.every(
                (p) => !this.enhancementIds![p] || this.enhancementDone![p],
              );
              if (allDone) {
                this.narrativePending = false;
              }
            },
            onTimeout: () => {
              if (equipmentRequestId !== this.equipmentRequestId) return;
              this.enhancementError![priority] =
                this.enhancementError![priority] ||
                "Enhancement polling timeout";
              this.enhancementLoading![priority] = false;
              this.enhancementDone![priority] = true;

              // Check if all are done
              const allDone = priorities.every(
                (p) => !this.enhancementIds![p] || this.enhancementDone![p],
              );
              if (allDone) {
                this.narrativePending = false;
              }
            },
            onAbort: () => {
              if (equipmentRequestId !== this.equipmentRequestId) return;
              this.enhancementLoading![priority] = false;
            },
          },
        );

        // Start polling (don't await - run in parallel)
        self[`_enhancementPoller_${priority}`].start().catch((e: any) => {
          if (equipmentRequestId !== this.equipmentRequestId) return;
          this.enhancementError![priority] = e.message || String(e);
          this.enhancementLoading![priority] = false;
        });
      }
    },
    cancelEnhancementPolling() {
      const self: any = this as any;
      const priorities = PRIORITIES;
      for (const priority of priorities) {
        const poller = self[`_enhancementPoller_${priority}`];
        if (poller?.isRunning?.()) {
          poller.stop("abort");
        }
      }
    },
    invalidateEquipmentSuggestions() {
      this.recommendationCache = {};
      this.activeCacheKey = null;
      this.usingCachedResults = false;
      this.equipmentRequestId += 1;
      this.loading = false;
      this.cancelEnhancementPolling();
    },
    async retryEnhancementPolling() {
      if (!this.enhancementIds) return;
      const self: any = this as any;
      const now = Date.now();
      // Basic sliding window: max 3 retries in 2 minutes
      if (!self._enhancementRetryTimestamps)
        self._enhancementRetryTimestamps = [] as number[];
      self._enhancementRetryTimestamps =
        self._enhancementRetryTimestamps.filter(
          (t: number) => now - t < 120000,
        );
      if (self._enhancementRetryTimestamps.length >= 3) {
        // Set error for all priorities
        const priorities = PRIORITIES;
        for (const priority of priorities) {
          if (this.enhancementIds[priority]) {
            this.enhancementError![priority] =
              "Retry limit reached. Please wait before trying again.";
          }
        }
        return;
      }
      self._enhancementRetryTimestamps.push(now);

      // Reset narrative fields for all priorities
      this.narratives = {};
      this.narrative = null;
      this.candidateNotes = {};
      this.missingData = {};
      this.tokenUsagePrompt = {};
      this.tokenUsageCompletion = {};
      this.latencyMs = {};
      this.modelId = {};
      this.enhancementStatus = {};
      this.enhancementAttempts = {};
      this.enhancementDone = {};
      this.enhancementError = {};
      this.narrativePending = true;

      // Stop all pollers
      const priorities = PRIORITIES;
      for (const priority of priorities) {
        const poller = self[`_enhancementPoller_${priority}`];
        if (poller) {
          poller.stop("abort");
          self[`_enhancementPoller_${priority}`] = null;
        }
      }

      return this.startEnhancementPolling();
    },
  },
});
